import { ButtonProps } from "antd";
import * as React from "react";
import styled from "styled-components";

import { COLORS } from "../constants";

import { Button } from "./Button";
import { FlexRow } from "./FlexLayout";

/**
 * The action row at the bottom of an OAuth settings page, once a configuration exists.
 *
 * Each connector's page had grown its own treatment of the same two or three buttons: a
 * filled blue Delete on Snowflake and Looker, the same one pinned to 160px on NetSuite, a
 * red text one on Tableau and Arrow Flight SQL, and "Remove" on the generic custom-OAuth
 * page. Tableau also put the destructive action to the left of Edit while every other page
 * put it last. These wrappers fix the button type, colour and label in one place so a new
 * connector page cannot re-drift.
 *
 * Order is primary, then secondary, then destructive: Save/Edit, Cancel, Delete.
 */
export const OAuthActions: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <FlexRow gap={1}>{children}</FlexRow>
);

/* `type` and `danger` are the whole point of these wrappers, so they are not overridable;
   everything else (loading, disabled, htmlType, onClick) passes through unchanged. */
type OAuthActionButtonProps = Omit<ButtonProps, "type" | "danger">;

/**
 * Wider than antd's 15px so "Save" and "Edit" do not come out visibly narrower than the
 * six-character "Delete" sitting beside them. Doubled `&&` to beat antd's `.ant-btn`
 * padding shorthand.
 */
const PrimaryActionButton = styled(Button)`
  &&.ant-btn {
    padding-left: 24px;
    padding-right: 24px;
  }
`;

export const OAuthSaveButton: React.FC<OAuthActionButtonProps> = ({
  children = "Save",
  ...props
}) => (
  <PrimaryActionButton type="primary" {...props}>
    {children}
  </PrimaryActionButton>
);

export const OAuthEditButton: React.FC<OAuthActionButtonProps> = ({
  children = "Edit",
  ...props
}) => (
  <PrimaryActionButton type="primary" {...props}>
    {children}
  </PrimaryActionButton>
);

export const OAuthCancelButton: React.FC<OAuthActionButtonProps> = ({
  children = "Cancel",
  ...props
}) => (
  <Button type="text" {...props}>
    {children}
  </Button>
);

/**
 * Outlined rather than borderless, so Delete reads as a button next to Save/Edit rather
 * than as a stray red label.
 *
 * Built on `type="text"` because this design system repaints antd's `default` button as
 * solid blue (see Button.tsx), which a `danger` default would inherit. Doubled `&&` for
 * specificity over that rule, matching the `&&.gray` precedent in Button.tsx.
 */
const DangerOutlineButton = styled(Button)`
  &&.ant-btn {
    background-color: ${COLORS.white};
    border-color: ${COLORS.red};
    color: ${COLORS.red};
  }

  &&.ant-btn:hover,
  &&.ant-btn:focus,
  &&.ant-btn:active {
    background-color: ${COLORS.redBg};
    border-color: ${COLORS.red};
    color: ${COLORS.red};
  }
`;

export const OAuthDeleteButton: React.FC<OAuthActionButtonProps> = ({
  children = "Delete",
  ...props
}) => (
  <DangerOutlineButton type="text" danger {...props}>
    {children}
  </DangerOutlineButton>
);
